import Swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';

/** Tono del icono de una ventana de confirmación (colores de .bo-st). */
export type BoSwalTone = 'ok' | 'warn' | 'bad' | 'brand';

/**
 * Icono de trazo (paths de Lucide, como los del menú) para `iconHtml` de
 * SweetAlert, dentro de la pastilla de color de `.bo-swal-icon` (styles.scss).
 */
export function boSwalIcon(
  paths: string[],
  tone: BoSwalTone,
  /** Con tono 'bad' el botón de confirmar va en rojo (acción destructiva). En
   *  avisos que solo se aceptan («OK»), pásalo a false para dejarlo en morado. */
  dangerConfirm = tone === 'bad',
): Pick<SweetAlertOptions, 'icon' | 'iconHtml' | 'customClass'> {
  return {
    icon: 'question',
    iconHtml: `<svg viewBox="0 0 24 24" aria-hidden="true">${paths.map((d) => `<path d="${d}"></path>`).join('')}</svg>`,
    customClass: {
      icon: tone === 'brand' ? 'bo-swal-icon' : `bo-swal-icon bo-swal-icon--${tone}`,
      ...(dangerConfirm ? { confirmButton: 'bo-swal-confirm--bad' } : {}),
    },
  };
}

/** Iconos usados en las confirmaciones. */
export const BO_SWAL_ICONS = {
  /** Chat (elegir conversación de soporte) */
  chat: ['M7.9 20A9 9 0 1 0 4 16.1L2 22Z'],
  /** Chat grupal (elegir conversación de soporte) */
  users: [
    'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2',
    'M9 3a4 4 0 1 0 0 8a4 4 0 1 0 0-8',
    'M22 21v-2a4 4 0 0 0-3-3.87',
    'M16 3.13a4 4 0 0 1 0 7.75',
  ],
  /** Devolver a la wallet (rechazar una retirada) */
  undo: ['M9 14 4 9l5-5', 'M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11'],
  /** Hecho / guardado */
  check: ['M20 6 9 17l-5-5'],
  /** Enviar (invitaciones a empleados) */
  send: [
    'M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z',
    'm21.854 2.147-10.94 10.939',
  ],
  /** Importar (traer de fuera al catálogo) */
  download: ['M12 15V3', 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'm7 10 5 5 5-5'],
  /** Error o aviso */
  alert: [
    'M12 2a10 10 0 1 0 0 20a10 10 0 1 0 0-20',
    'M12 8v4',
    'M12 16h.01',
  ],
};

/**
 * Confirmación que guarda con la ventana abierta, como la decisión de fraude:
 * mientras `save` trabaja, el botón muestra «{savingText}» con spinner y todo
 * queda bloqueado; al terminar bien enseña «✓ {doneText}» un instante y se
 * cierra. Si `save` falla, la ventana se cierra y el error se devuelve en
 * `error` para que quien llama lo trate como antes.
 */
export async function boSwalConfirmAndSave<T>(
  options: SweetAlertOptions,
  save: (value: any) => Promise<T>,
  texts: { saving: string; done: string },
): Promise<{ result: SweetAlertResult; saved?: T; error?: unknown }> {
  let saving = false;
  let saved: T | undefined;
  let error: unknown;

  const result = await Swal.fire({
    showCloseButton: true,
    ...options,
    allowOutsideClick: () => !saving,
    allowEscapeKey: () => !saving,
    preConfirm: (value: any) => {
      const confirmButton = Swal.getConfirmButton();
      // Texto original del botón (ya traducido por quien llama) para
      // restaurarlo si falla: sin textos fijos aquí.
      const confirmHtml = confirmButton?.innerHTML ?? '';
      saving = true;
      Swal.disableButtons();
      Swal.getInput()?.setAttribute('disabled', '');
      // La ✕ no la desactiva disableButtons(): si se cerrara mientras guarda,
      // quien llama vería isConfirmed = false y perdería el resultado.
      Swal.getCloseButton()?.setAttribute('disabled', '');
      if (confirmButton) {
        confirmButton.innerHTML = `<span class="bo-spinner" aria-hidden="true"></span> ${texts.saving}`;
      }
      return save(value)
        .then(
          (value) =>
            new Promise<boolean>((resolve) => {
              saved = value;
              if (confirmButton) {
                confirmButton.innerHTML = `<svg class="bo-swal-done" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6 9 17l-5-5"></path></svg> ${texts.done}`;
              }
              setTimeout(() => resolve(true), 700);
            }),
        )
        .catch((err) => {
          error = err;
          if (confirmButton) confirmButton.innerHTML = confirmHtml;
          return true;
        })
        .finally(() => {
          saving = false;
        });
    },
  });
  return { result, saved, error };
}
